<template>
  <DrawerModal
    class="CrewModalView"
    :aria-label="crew ? `Crew details for ${crew.name}` : 'Crew details'"
    :aria-busy="!isCrewLoaded"
  >
    <div
      class="CrewModalView__layout"
    >
      <div class="CrewModalView__header">
        <h1
          class="CrewModalView__title"
          data-testid="name"
        >
          <template v-if="crew?.name">
            {{ crew.name }}
          </template>
          <template v-else>
          &nbsp;
          </template>
        </h1>
        <address class="CrewModalView__location">
          <Icon name="location" />
          <span
            class="CrewModalView__locationLabel"
            data-testid="location"
          >
            <template v-if="crew?.location">{{ crew.location }}</template>
            <template v-else-if="crew">Unknown</template>
            <template v-else>
          &nbsp;
            </template>
          </span>
        </address>
      </div>
      <section>
        <h2 class="CrewModalView__statsTitle">
          Stats
        </h2>
        <StatRow>
          <StatRowItem
            data-testid="playerCount"
            :label="playerCountLabel"
            :modelValue="playerCount"
            :aria-label="`${playerCount} ${playerCountLabel}`"
          />
          <StatRowItem
            data-testid="runCount"
            :label="runCountLabel"
            :modelValue="runCount"
            :aria-label="`${runCount} ${runCountLabel}`"
          />
          <StatRowItem
            data-testid="seasonCount"
            :label="seasonCountLabel"
            :modelValue="seasonCount"
            :aria-label="`${seasonCount} ${seasonCountLabel}`"
          />
        </StatRow>
      </section>
      <Transition>
        <HeightRangeChart
          v-if="crew && crew.height_counts.length > 0"
          class="CrewModalView__heightRangeChart"
          :modelValue="crew.height_counts"
        />
      </Transition>
    </div>
  </DrawerModal>
</template>

<script setup lang="ts">
import DrawerModal from "@/components/DrawerModal.vue";
import HeightRangeChart from "@/components/HeightRangeChart.vue";
import Icon from "@/components/Icon.vue";
import StatRow from "@/components/StatRow.vue";
import StatRowItem from "@/components/StatRowItem.vue";
import useCrewStore from "@/stores/useCrewStore";
import { PublicCrewProfile } from "@shared/lib/database/query.types";
import { computed, onBeforeMount, ref } from "vue";

const {
  crewId,
} = defineProps<{
  crewId: string,
}>();

const {
  getCrew,
  fetchCrew,
} = useCrewStore();

const crew = ref<PublicCrewProfile | null>(null);
const isCrewLoaded = ref(false);

onBeforeMount(async () => {
  const cachedCrew = getCrew(crewId);
  if (cachedCrew) {
    crew.value = {
      player_count: 0,
      run_count: 0,
      season_count: 0,
      height_counts: [],
      ...cachedCrew,
    };
  }

  isCrewLoaded.value = !!crew.value;

  try {
    const _crew = await fetchCrew(crewId);
    if (_crew) crew.value = _crew;

    isCrewLoaded.value = true;
  } catch (error) {
    if (!crew.value) alert((error as Error).message);
  }
});

const playerCount = computed(() => crew.value?.player_count ?? 0);
const runCount = computed(() => crew.value?.run_count ?? 0);
const seasonCount = computed(() => crew.value?.season_count ?? 0);

const playerCountLabel = computed(() => {
  return playerCount.value === 1 ? "Player" : "Players";
});

const runCountLabel = computed(() => {
  return runCount.value === 1 ? "Run" : "Runs";
});

const seasonCountLabel = computed(() => {
  return seasonCount.value === 1 ? "Season" : "Seasons";
});
</script>

<style lang="scss" scoped>
.CrewModalView__layout {
  min-height: 100%;
  padding-bottom: calc(var(--safeAreaInsetBottom) + 24px);
  background-color: var(--themeColorSurface);
}

.CrewModalView__header {
  display: grid;
  padding: 16px 16px 24px;

  background-color: var(--themeColorPrimary);
  color: var(--themeColorPrimaryForeground);
}

.CrewModalView__title {
  font-size: 32px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
  line-height: 1.2;
}

.CrewModalView__location {
  --iconSize: 16px;
  display: flex;
  gap: 6px;
  align-items: center;
  margin-top: 6px;

  color: var(--themeLightTransparent700);

  font-size: 18px;
  font-weight: var(--fontWeightMedium);
  font-stretch: var(--fontWidthExtended);
}

.CrewModalView__locationLabel {
  position: relative;
  top: max(1px, var(--fontSafariOffset));

  overflow: hidden;

  text-overflow: ellipsis;
  white-space: nowrap;
}

.CrewModalView__statsTitle {
  display: flex;
  align-items: center;
  height: 44px;
  padding: 0 16px;

  background-color: var(--themeColorSecondary);
  color: var(--themeColorSecondaryForeground);

  font-size: 14px;
  font-weight: var(--fontWeightMedium);
  text-transform: uppercase;
}

.CrewModalView__heightRangeChart {
  border-bottom: 1px solid var(--themeColorNeutral100);
  transition: opacity 350ms;

  &.v-enter-from,
  &.v-leave-to {
    opacity: 0;
  }
}
</style>
